// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import { isNativeApp } from "./server";

// Push (FCM) wiring for the native app. On the web it's a no-op.
//
// Status: the client is fully wired (permission → register → token → onToken →
// foreground messages) but GATED OFF by default, because register() throws a native
// (uncaught) exception when Firebase isn't configured. To enable push:
//   1. Create a Firebase project and drop its google-services.json into
//      android/app/, then apply the google-services Gradle plugin (classpath
//      com.google.gms:google-services in android/build.gradle + `apply plugin` in
//      android/app/build.gradle). See android README.
//   2. Flip PUSH_ENABLED to true (or set localStorage "zalmoxis.push" = "1").
//   3. Implement the server-side sender: the homeserver stores each device's token
//      (POST /client/v1/pushers, already called) and pushes via FCM HTTP v1 on new
//      events for offline members.
const PUSH_ENABLED = false;

export interface PushHandlers {
  // onToken receives the FCM registration token to report to the homeserver.
  onToken: (token: string) => void;
  // onMessage fires when a push arrives while the app is in the foreground.
  onMessage?: (title: string, body: string, data: Record<string, unknown>) => void;
}

export async function initPush(handlers: PushHandlers): Promise<void> {
  if (!isNativeApp()) {
    return;
  }
  // Off until a Firebase project is configured (see the note above) — otherwise the
  // native register() crashes the process.
  if (!PUSH_ENABLED && localStorage.getItem("zalmoxis.push") !== "1") {
    return;
  }
  const { PushNotifications } = await import("@capacitor/push-notifications");
  try {
    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === "prompt" || perm.receive === "prompt-with-rationale") {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive !== "granted") {
      return;
    }
    await PushNotifications.addListener("registration", (token) => handlers.onToken(token.value));
    await PushNotifications.addListener("registrationError", () => undefined);
    await PushNotifications.addListener("pushNotificationReceived", (n) =>
      handlers.onMessage?.(n.title ?? "", n.body ?? "", (n.data as Record<string, unknown>) ?? {}),
    );
    await PushNotifications.register();
  } catch {
    // No Firebase config / unsupported platform — push is unavailable; the app runs.
  }
}
